import type {Component, PanelLifecycleContext} from 'panel';
import {i18n, TOptions} from 'i18next';
import moment, {Moment} from 'moment';
import {Moment as MomentTimezone} from 'moment-timezone';

import {reporter} from 'common/report/error-reporting';
import type {Subscribable} from 'common/types/context/subscribable';

import type {LocaleSafeString, Translatable, TranslationStore} from './index';
import type {I18nScope, ScopeTranslationKey, StorageKey, TranslationKeyMap} from './scope';
import {SCOPED_MISS_MESSAGE_PREFIX} from './scoped-miss';
import timeTranslationKeys from './store.i18n';

export class LocalizationStore implements PanelLifecycleContext, Subscribable {
  private connectedComponents = new Set<Component<any>>();
  private subscriptions = new Set<() => void>();

  declare private languageOverride: string | undefined;
  private scopedStores = new WeakMap<I18nScope<any>, ScopedLocalizationStore<any>>();
  private updateScheduled = false;
  private reportedScopedMisses = new Set<string>();

  constructor(private i18next: i18n) {
    i18next.on(`languageChanged`, () => {
      this.updateComponents();

      moment.locale(this.language);
    });
    // translations that arrive after the language was selected (a scope loaded with its route chunk)
    i18next.store.on(`added`, (language: string) => {
      if (language !== `en` && !this.updateScheduled) {
        this.updateScheduled = true;
        queueMicrotask(() => {
          this.updateScheduled = false;
          this.updateComponents();
        });
      }
    });
  }

  get language() {
    return this.i18next.language;
  }

  /** Translates a storage key that a caller produced with `scope.qualify(key)`. */
  translateKey = (key: StorageKey, opts?: TOptions): LocaleSafeString => this.translateRawKey(key, opts);

  private translateRawKey(key: string, opts?: TOptions): LocaleSafeString {
    // @ts-ignore -- type excessive; ok to disable here since we're wrapping it safely
    return this.i18next.t(key, {
      lng: this.languageOverride,
      ...opts,
    }) as LocaleSafeString;
  }

  /** Returns this store narrowed to one scope's keys. Cached, so the identity is stable across renders. */
  scoped<Keys extends TranslationKeyMap>(scope: I18nScope<Keys>): ScopedLocalizationStore<Keys> {
    let scopedStore = this.scopedStores.get(scope) as ScopedLocalizationStore<Keys> | undefined;
    if (!scopedStore) {
      scopedStore = new ScopedLocalizationStore(this, scope);
      this.scopedStores.set(scope, scopedStore);
    }
    return scopedStore;
  }

  /**
   * Translates a scope-relative key. The types only allow keys of the scope, so a miss means a key built at runtime
   * that the scope does not define: it renders the qualified key and is reported once.
   */
  translateScopedKey(
    scope: {readonly id: string; has(key: string): boolean},
    key: string,
    opts?: TOptions,
  ): LocaleSafeString {
    const qualifiedKey = `${scope.id}.${key}`;
    if (scope.has(key)) {
      return this.translateRawKey(qualifiedKey, opts);
    }

    if (!this.reportedScopedMisses.has(qualifiedKey)) {
      reporter.error(`${SCOPED_MISS_MESSAGE_PREFIX} ${qualifiedKey}`);
      this.reportedScopedMisses.add(qualifiedKey);
    }
    return qualifiedKey as LocaleSafeString;
  }

  /** Maybe translates a string if it's a function. Otherwise, returns the original string. */
  print = (translatable: Translatable | undefined | null, forceLng?: string): string =>
    this.printWith(this, translatable, forceLng);

  /** Runs a translatable with the store `print` was invoked on. */
  printWith(
    invokedOn: TranslationStore,
    translatable: Translatable | undefined | null,
    forceLng: string | undefined,
  ): string {
    if (!translatable) {
      return ``;
    }
    this.languageOverride = forceLng;
    const result = typeof translatable === `function` ? translatable(invokedOn) : translatable;
    this.languageOverride = undefined;
    return result;
  }

  /**
   * Joins a list of translatables using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat/ListFormat#options
   */
  formatList = (
    items: Translatable[],
    type?: Intl.ListFormatOptions[`type`],
    style?: Intl.ListFormatOptions[`style`],
  ) => {
    const translatedItems = items.map((item) => this.print(item));

    return new Intl.ListFormat(this.language, {type, style}).format(translatedItems);
  };

  /**
   * Returns the "and" conjunction word using the current locale.
   * NOTE: This is for edge cases where formatList doesn't work. Use formatList where at all possible.
   */
  getConjunctionWord = () => {
    const parts = new Intl.ListFormat(this.language, {type: `conjunction`, style: `long`}).formatToParts([`1`, `2`]);
    return parts.find((p) => p.type === `literal`)?.value?.trim() || ``;
  };

  /**
   * Formats a date using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
   */
  formatDate = (date: Date | Moment | MomentTimezone, options: Intl.DateTimeFormatOptions) => {
    // Make sure we don't lose timezone information.
    if (moment.isMoment(date) && `tz` in date) {
      options.timeZone = (date as MomentTimezone).tz();
    }
    const jsDate = date instanceof Date ? date : date.toDate();

    try {
      return new Intl.DateTimeFormat(this.language, options).format(jsDate);
    } catch (err) {
      console.warn(`Failed to format date`, err);
      return jsDate.toLocaleString();
    }
  };

  /**
   * Formats a date to parts using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
   */
  formatDateToParts = (date: Date | Moment, options: Intl.DateTimeFormatOptions) => {
    const jsDate = date instanceof Date ? date : date.toDate();

    return new Intl.DateTimeFormat(this.language, options).formatToParts(jsDate);
  };

  /**
   * Formats a relative time unit using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#options
   */
  formatRelativeTime = (value: number, unit: Intl.RelativeTimeFormatUnit, options?: Intl.RelativeTimeFormatOptions) => {
    return new Intl.RelativeTimeFormat(this.language, options).format(value, unit);
  };

  /**
   * Returns a localized string representing the difference between 2 times, similar to Moment#from.
   */
  timeFrom = (fromTime: Date | Moment, toTime: Date | Moment) => {
    const from = fromTime instanceof Date ? moment(fromTime) : fromTime;
    const to = toTime instanceof Date ? moment(toTime) : toTime;

    // less than a minute ago; in less than a minute
    const diffSeconds = from.diff(to, `seconds`);
    if (Math.abs(diffSeconds) < 60) {
      return this.scoped(timeTranslationKeys).t(`time.less-than-minute-${from.isBefore(to) ? `past` : `future`}`);
    }

    // X minute(s) ago; in X minute(s)
    const diffMinutes = from.diff(to, `minutes`);
    if (Math.abs(diffMinutes) < 60) {
      return this.formatRelativeTime(diffMinutes, `minutes`);
    }

    // X hour(s) ago; in X hour(s)
    const diffHours = from.diff(to, `hours`);
    if (Math.abs(diffHours) < 24) {
      return this.formatRelativeTime(diffHours, `hours`);
    }

    // X day(s) ago; in X day(s)
    const diffDays = from.diff(to, `days`);
    const diffMonths = from.diff(to, `months`);
    if (Math.abs(diffMonths) < 1) {
      return this.formatRelativeTime(diffDays, `days`);
    }

    // X month(s) ago; in X month(s)
    const diffYears = from.diff(to, `years`);
    if (Math.abs(diffYears) < 1) {
      return this.formatRelativeTime(diffMonths, `months`);
    }

    // X year(s) ago; in X year(s)
    return this.formatRelativeTime(diffYears, `years`);
  };

  /**
   * Returns a localized string representing the difference between a time and now, similar to Moment#fromNow.
   */
  timeFromNow = (time: Date | Moment) => this.timeFrom(time, moment());

  public subscribe(callback: () => void): this {
    this.subscriptions.add(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions.delete(callback);
    return this;
  }

  public bindToComponent(component: Component<any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents = () => {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  };
}

/**
 * A `LocalizationStore` whose `t` only accepts the keys of one scope. Formatting helpers are bound delegates, so
 * `const {t, print} = useTranslation(scope)` works.
 */
export class ScopedLocalizationStore<Keys extends TranslationKeyMap = TranslationKeyMap> {
  constructor(
    private readonly root: LocalizationStore,
    private readonly scope: I18nScope<Keys>,
  ) {}

  /** Gets translation for a key of this scope with options. */
  t = (key: ScopeTranslationKey<Keys>, opts?: TOptions): LocaleSafeString =>
    this.root.translateScopedKey(this.scope, key, opts);

  /**
   * Translates a full storage key, for keys that callers supply from their own dictionaries. Prefer accepting a
   * `Translatable` where the callee does not need to interpolate markup.
   */
  translateKey: LocalizationStore[`translateKey`] = (...args) => this.root.translateKey(...args);

  /** Whether the key is defined in this scope. For keys that are only known at runtime. */
  has = (key: string): key is ScopeTranslationKey<Keys> => this.scope.has(key);

  /** Returns the root store narrowed to another scope. */
  scoped: LocalizationStore[`scoped`] = (scope) => this.root.scoped(scope);

  print: LocalizationStore[`print`] = (translatable, forceLng) => this.root.printWith(this, translatable, forceLng);
  formatList: LocalizationStore[`formatList`] = (...args) => this.root.formatList(...args);
  formatDate: LocalizationStore[`formatDate`] = (...args) => this.root.formatDate(...args);
  formatDateToParts: LocalizationStore[`formatDateToParts`] = (...args) => this.root.formatDateToParts(...args);
  formatRelativeTime: LocalizationStore[`formatRelativeTime`] = (...args) => this.root.formatRelativeTime(...args);
  timeFrom: LocalizationStore[`timeFrom`] = (...args) => this.root.timeFrom(...args);
  timeFromNow: LocalizationStore[`timeFromNow`] = (...args) => this.root.timeFromNow(...args);
  getConjunctionWord: LocalizationStore[`getConjunctionWord`] = () => this.root.getConjunctionWord();

  get language() {
    return this.root.language;
  }
}

/**
 * A scoped store of a scope the receiving code does not know. Its `t` accepts no key: translate with a store
 * obtained from `i18n.scoped(scope)`.
 */
export type AnyScopedLocalizationStore = Omit<ScopedLocalizationStore<TranslationKeyMap>, `t` | `has`> & {
  t: (key: never, opts?: TOptions) => LocaleSafeString;
};
